import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrAvatar from "./GrAvatar";
import GrIcon from "./GrIcon";
import icons from "../icons";
import { gr } from "../tokens";

// Tall story tile (9:16) for a row of posts, like the stories row of social apps. The post photo
// fills the tile (with text, its first two lines sit above the name, white on a dark fade). A post
// without a photo is a light tile (brand-soft with a thin line) with its text in ink, centred, so a
// row of text posts is not a wall of solid teal; its name sits at the bottom in ink. The author's
// photo sits top-left in a brand ring. `play` adds a round play mark (video posts, with or without a
// still). The whole tile is one button: it opens the post; on hover it lifts a little (not with
// reduced motion).
const clamp = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const playMarkSx = (size, background = gr.color.overlay) => ({
  width: size,
  height: size,
  flex: "none",
  display: "grid",
  placeItems: "center",
  borderRadius: gr.radius.pill,
  background,
  color: gr.color.onBrand,
  "& svg": { width: `calc(${size} * 0.62)`, height: `calc(${size} * 0.62)` },
});

export default function GrStoryTile({
  image,
  text,
  play = false,
  avatarSrc,
  name,
  meta,
  onClick,
  label,
  sx,
}) {
  // No photo: light tile, dark text (the name strip needs no fade).
  const light = !image;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label || name}
      sx={{
        position: "relative",
        display: "block",
        width: "100%",
        aspectRatio: "9 / 16",
        p: 0,
        border: light ? `1px solid ${gr.color.line}` : 0,
        borderRadius: gr.radius.tile,
        overflow: "hidden",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: gr.font.family,
        color: light ? gr.color.ink : gr.color.onBrand,
        background: light ? gr.color.brandSoft : gr.color.brandDark,
        transition: "transform .2s ease, box-shadow .2s ease",
        "&:hover": {
          transform: `translateY(calc(-1 * ${gr.space[1]}))`,
          boxShadow: gr.shadow.raised,
        },
        "& .gr-story-image": { transition: "transform .25s ease" },
        "&:hover .gr-story-image": { transform: "scale(1.06)" },
        "&:focus-visible": { outline: "none", boxShadow: gr.shadow.focus },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
          "& .gr-story-image": { transition: "none" },
          "&:hover .gr-story-image": { transform: "none" },
        },
        ...sx,
      }}
    >
      {image ? (
        <Box
          component="img"
          className="gr-story-image"
          src={image}
          alt=""
          loading="lazy"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : null}

      {/* Post without a photo: its text (and the play mark for a video) in the middle. */}
      {light && (text || play) ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: gr.space[2],
            px: gr.space[3],
            pt: gr.space[10],
            pb: gr.space[12],
            textAlign: "center",
          }}
        >
          {play ? (
            <Box aria-hidden="true" sx={playMarkSx(gr.space[8], gr.color.brandStrong)}>
              <GrIcon icon={icons.playArrow} />
            </Box>
          ) : null}
          {text ? (
            <Box
              component="span"
              sx={{
                ...clamp(play ? 2 : 4),
                fontSize: gr.font.size.sm,
                fontWeight: gr.font.weight.medium,
                lineHeight: gr.font.lineHeight.tight,
                wordBreak: "break-word",
              }}
            >
              {text}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {image && play ? (
        <Box
          aria-hidden="true"
          sx={{
            ...playMarkSx(gr.space[10]),
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <GrIcon icon={icons.playArrow} />
        </Box>
      ) : null}

      {/* Photo: a doubled fade under the name so white text stays readable on light images. */}
      {image ? (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(to top, ${gr.color.overlay}, transparent 70%), linear-gradient(to top, ${gr.color.overlay}, transparent 45%)`,
          }}
        />
      ) : null}

      <GrAvatar
        src={avatarSrc}
        name={name}
        size="sm"
        sx={{
          position: "absolute",
          top: gr.space[3],
          left: gr.space[3],
          boxShadow: `0 0 0 2px ${gr.color.surface}, 0 0 0 4px ${gr.color.brand}`,
        }}
      />

      <Box sx={{ position: "absolute", left: gr.space[2], right: gr.space[2], bottom: gr.space[2] }}>
        {/* Photo with text: the text's first two lines above the name. */}
        {image && text ? (
          <Box
            component="span"
            sx={{
              ...clamp(2),
              mb: gr.space[1],
              fontSize: gr.font.size.xs,
              lineHeight: gr.font.lineHeight.tight,
              wordBreak: "break-word",
            }}
          >
            {text}
          </Box>
        ) : null}
        {name ? (
          <Box
            component="span"
            sx={{
              ...clamp(2),
              fontSize: gr.font.size.xs,
              fontWeight: gr.font.weight.semibold,
              lineHeight: gr.font.lineHeight.tight,
              wordBreak: "break-word",
            }}
          >
            {name}
          </Box>
        ) : null}
        {meta ? (
          <Box
            component="span"
            sx={{
              ...clamp(1),
              mt: "2px",
              fontSize: gr.font.size.xs,
              color: light ? gr.color.ink2 : "inherit",
            }}
          >
            {meta}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

GrStoryTile.propTypes = {
  image: PropTypes.string,
  text: PropTypes.string,
  play: PropTypes.bool,
  avatarSrc: PropTypes.string,
  name: PropTypes.string,
  meta: PropTypes.node,
  onClick: PropTypes.func,
  label: PropTypes.string,
  sx: PropTypes.object,
};
